import { Button, Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { IField, createTextField } from '../FieldRenderer';
import { FCNC, Form, TextField, TwoActionButtons, IActionButton, renderErrorToErrorMessage } from '../../ui';
import { MutationResult } from './MutationResult';
import { ReactNode } from 'react';
import { DotNotationHelperText } from './DotNotationHelperText';

export interface IAttributeConfiguratorProps {
    attributes: IField[] | undefined;
    originalAttributes?: IField[];
    onChange: (fields: IField[]) => void;
    onSave: () => void;
    cancelButton: IActionButton;
    mutation: MutationResult;
    readOnly?: boolean;
    title: string;
    isLoading: boolean;
    dataBindingKeyHelperText?: ReactNode;
}

function isAttributeListDirty(current: IField[], original: IField[] | undefined) {
    return JSON.stringify(current) !== JSON.stringify(original ?? []);
}

export const AttributeConfigurator: FCNC<IAttributeConfiguratorProps> = ({
    attributes,
    originalAttributes,
    onChange,
    onSave,
    cancelButton,
    mutation,
    readOnly,
    title,
    isLoading,
    dataBindingKeyHelperText = <DotNotationHelperText />,
}) => {
    const theme = useTheme();
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
    const fields = attributes ?? [];
    const isDirty = isAttributeListDirty(fields, originalAttributes);

    const defaultFieldValues = {
        label: '',
        dataBindingKey: '',
        defaultValue: '',
    };

    const editField = <K extends keyof IField>(id: string, key: K, value: IField[K]) => {
        const newFields = fields.map((field) => (field.id === id ? { ...field, [key]: value } : field));
        onChange(newFields);
    };

    const addField = () => {
        const newFields = [...fields, createTextField(defaultFieldValues)];
        onChange(newFields);
    };

    const removeField = (id: string) => {
        const newFields = fields.filter((field) => field.id !== id);
        onChange(newFields);
    };

    return (
        <Form
            testId="suppliermanagement-companyvendorattributes-form"
            loadingMessage={isLoading && `Loading ${title}`}
            savingMessage={mutation.isLoading ? `Saving ${title}` : null}
            successMessage={mutation.isSuccess ? `${title} Saved!` : null}
            errorMessage={renderErrorToErrorMessage(mutation.error)}
            onSubmit={onSave}
            formButtons={
                !readOnly && (
                    <TwoActionButtons
                        submitButton={{
                            text: 'Save',
                            isDisabled: !isDirty,
                        }}
                        cancelButton={{
                            ...cancelButton,
                            isDisabled: !isDirty,
                        }}
                        isLoading={mutation.isLoading}
                    />
                )
            }
            columns={[
                <>
                    <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                        <Typography variant="h6" component="h3">
                            {title}
                        </Typography>
                        {dataBindingKeyHelperText}
                    </Stack>
                    <Box
                        sx={{
                            maxHeight: 'min(240px, 30vh)',
                            overflowY: 'auto',
                            pr: 0.5,
                        }}
                    >
                        {fields.map((attr) => {
                            const fieldNameErrorMessages: string[] = [];

                            const fieldNameIsDuplicate = fields
                                .filter((field) => field.id !== attr.id && field.label)
                                .some((field) => field.label === attr.label);

                            if (fieldNameIsDuplicate) {
                                fieldNameErrorMessages.push('Field Name must be unique');
                            }

                            const dataBindingKeyErrorMessages: string[] = [];

                            const dataBindingKeyIsDuplicate = fields
                                .filter((field) => field.id !== attr.id && field.dataBindingKey)
                                .some((field) => field.dataBindingKey === attr.dataBindingKey);

                            if (dataBindingKeyIsDuplicate) {
                                dataBindingKeyErrorMessages.push('Data Binding Key must be unique');
                            }

                            return (
                                <Stack
                                    direction={isMdDown ? 'column' : 'row'}
                                    spacing={2}
                                    mb={2}
                                    key={attr.id}
                                    alignItems={isMdDown ? 'stretch' : 'center'}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <TextField
                                            key={attr.id}
                                            id={`suppliermanagement-attributeformlayout-configurationattribute-label-${attr.id}`}
                                            label="Field Name"
                                            value={attr.label}
                                            onChange={(ev) => editField(attr.id, 'label', ev.target.value)}
                                            readonly={readOnly}
                                            error={fieldNameErrorMessages.length > 0}
                                            helperText={fieldNameErrorMessages.join('\n')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <TextField
                                            key={attr.id}
                                            id={`suppliermanagement-attributeformlayout-configurationattribute-xpath-${attr.id}`}
                                            label="Data Binding Key"
                                            value={attr.dataBindingKey}
                                            onChange={(ev) => editField(attr.id, 'dataBindingKey', ev.target.value)}
                                            readonly={readOnly}
                                            error={dataBindingKeyErrorMessages.length > 0}
                                            helperText={dataBindingKeyErrorMessages.join('\n')}
                                        />
                                    </Box>
                                    {!readOnly && (
                                        <Box>
                                            <IconButton
                                                onClick={() => removeField(attr.id)}
                                                color="error"
                                                aria-label="Delete field"
                                            >
                                                <DeleteOutlinedIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Stack>
                            );
                        })}
                        {fields.length === 0 && (
                            <Typography variant="body1" py={4}>
                                Use the Add Field button to add a custom attribute.
                            </Typography>
                        )}
                    </Box>
                    {!readOnly && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={addField}>
                            Add Field
                        </Button>
                    )}
                </>,
            ]}
            requiredMessage={false}
        />
    );
};
